/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

interface EmailVerifiedRouteProps {
  children: React.ReactNode;
  fallbackPath?: string; // Where to redirect if not verified (default: /home)
}

/**
 * Route guard that ensures the user has a verified email address
 * If user is not verified, shows a blocking dialog instead of navigating
 */
const EmailVerifiedRoute: React.FC<EmailVerifiedRouteProps> = ({ 
  children, 
  fallbackPath = '/home' 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle dialog opening for unverified users - must be at top level
  React.useEffect(() => {
    // Only show dialog if user is authenticated but not verified
    if (isAuthenticated && user && !user.isEmailVerified) {
      setDialogOpen(true);
    }
  }, [isAuthenticated, user]);

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If email is verified, allow access
  if (user?.isEmailVerified) {
    return <>{children}</>;
  }

  const handleResendEmail = async () => {
    if (!user?.email) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      if (response.ok) {
        // Success handled by the dialog close
      } else {
        const errorData = await response.json();
        logger.error('Failed to resend verification email:', errorData);
      }
    } catch (error) {
      logger.error('Error resending verification email:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    // Small delay before redirect to ensure dialog closes smoothly
    setTimeout(() => {
      window.location.href = fallbackPath;
    }, 300);
  };

  return (
    <>
      {/* Show the content briefly before dialog appears */}
      {children}
      
      <Dialog 
        open={dialogOpen} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BlockIcon color="warning" />
            Email Verification Required
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              You need to verify your email address before you can create or participate in debates.
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We've sent a verification link to <strong>{user?.email}</strong>. 
            Please check your email and click the verification link to continue.
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              What you can do while unverified:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="View public debates" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Browse leaderboards" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Explore the CUBE" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BlockIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Create or participate in debates" />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Back to Dashboard
          </Button>
          <Button 
            onClick={handleResendEmail}
            color="primary"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {isLoading ? 'Sending...' : 'Resend Verification Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailVerifiedRoute; 